import React, { useEffect, useRef } from "react";
import "./template.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { IoMdTrendingUp } from "react-icons/io";
import { IoMdTrendingDown } from "react-icons/io";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, ResponsiveContainer } from 'recharts';

export const Template = ({ data, close }) => {
    const overviewRef = useRef(null);
    const colors = ['#49AF7C', '#89D4AF', '#C4EED9'];

    const exportPDF = async () => {
        try {
            const overviewDiv = overviewRef.current;

            // Capture options
            const options = {
                useCORS: true,
                scrollX: 0,
                scrollY: 0,
                width: overviewDiv.scrollWidth,
                height: overviewDiv.scrollHeight + 10,
            };

            // Generate canvas
            const canvas = await html2canvas(overviewDiv, options);

            // Convert canvas to image data
            const imgData = canvas.toDataURL('image/png');

            // Create PDF using jsPDF
            const pdf = new jsPDF('p', 'pt', [canvas.width, canvas.height]); // Use 'pt' for dimensions in pixels

            // Add margin to the image
            const margin = 20; // adjust the margin value as needed
            pdf.addImage(imgData, 'PNG', margin, margin, canvas.width - margin * 2, canvas.height - margin * 2);

            pdf.save('download.pdf'); // Save PDF
            close();
        } catch (error) {
            console.error('Error exporting PDF:', error);
        }
    };

    useEffect(() => {
        exportPDF();
    }, []);

    return (
        <div className="container" id="overview-temp" ref={overviewRef}>
            <div className="rinfo-divs">
                <div className="info-div">
                    <div className="info-top">
                        <span>Signups</span>
                    </div>
                    <div className="info-bottom">
                        <p className="info-data">{data.signups.total}</p>
                        <div className="info-trend">
                            {
                                data.signups.total > data.signups.previous ?
                                    <>
                                        <IoMdTrendingUp size={18} color={"#49AF7C"} />
                                        <span className="green">{(((data.signups.total - data.signups.previous) * 100) / data.signups.total).toFixed(2)}%</span>
                                    </> : <>
                                        <IoMdTrendingDown size={18} color={"red"} />
                                        <span className="red">{(((data.signups.previous - data.signups.total) * 100) / data.signups.total).toFixed(2)}%</span>
                                    </>
                            }
                            <span>Compared to last month</span>
                        </div>
                    </div>
                </div>
                <div className="info-div">
                    <div className="info-top">
                        <span>Active Users</span>
                    </div>
                    <div className="info-bottom">
                        <p className="info-data">{data.active.total}</p>
                        <div className="info-trend">
                            {
                                data.active.total > data.active.previous ?
                                    <>
                                        <IoMdTrendingUp size={18} color={"#49AF7C"} />
                                        <span className="green">{(((data.active.total - data.active.previous) * 100) / data.active.total).toFixed(2)}%</span>
                                    </> : <>
                                        <IoMdTrendingDown size={18} color={"red"} />
                                        <span className="red">{(((data.active.previous - data.active.total) * 100) / data.active.total).toFixed(2)}%</span>
                                    </>
                            }
                            <span>Compared to yesterday</span>
                        </div>
                    </div>
                </div>
                <div className="info-div">
                    <div className="info-top">
                        <span>Logins this month</span>
                    </div>
                    <div className="info-bottom">
                        <p className="info-data">{data.logins.total}</p>
                        <div className="info-trend">
                            {
                                data.logins.total > data.logins.previous ?
                                    <>
                                        <IoMdTrendingUp size={18} color={"#49AF7C"} />
                                        <span className="green">{(((data.logins.total - data.logins.previous) * 100) / data.logins.total).toFixed(2)}%</span>
                                    </> : <>
                                        <IoMdTrendingDown size={18} color={"red"} />
                                        <span className="red">{(((data.logins.previous - data.logins.total) * 100) / data.logins.total).toFixed(2)}%</span>
                                    </>
                            }
                            <span>Compared to yesterday</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="painpoint-div">
                <div className="painpoint-top">
                    <p>Major Pain Points</p>
                </div>
                <div className="overview-graph">
                    {data.overviewItems.length == 0 ? (
                        <h3 style={{ margin: "0px auto" }}>No Data Found</h3>
                    ) :
                        <div>{JSON.stringify(data.overviewItems)}</div>
                    }
                </div>
            </div>
            <div className="info-divs">
                <div className="info-div graph">
                    <div className="info-top">
                        <span>Content Trends</span>
                    </div>
                    <div style={{ width: '100%', height: '300px' }}>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                                data={data.contentTrends}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                                <XAxis dataKey="name" />
                                <YAxis />
                                {
                                    <Bar dataKey="value" name="Value">
                                        {data.contentTrends.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                                        ))}
                                    </Bar>
                                }
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="info-div graph">
                    <div className="info-top">
                        <span>Engagement by type</span>
                    </div>
                    <div style={{ width: '100%', height: '300px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={data.engagement}
                                layout="vertical"
                                margin={{ left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
                                <XAxis type="number" />
                                <YAxis dataKey="name" type="category" />
                                {
                                    <Bar dataKey="value" name="Value">
                                        {data.engagement.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                                        ))}
                                    </Bar>
                                }
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="info-div graph">
                    <div className="info-top">
                        <span>Time Distribution of app usage</span>
                    </div>
                    <div style={{ width: '100%', height: '300px' }} >
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={data.distribution}
                                layout="vertical"
                                margin={{ left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
                                <XAxis type="number" />
                                <YAxis dataKey="name" type="category" />
                                {
                                    <Bar dataKey="value" name="Value">
                                        {data.distribution.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                                        ))}
                                    </Bar>
                                }
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="info-div graph">
                    <div className="info-top">
                        <span>Most Engaged Employees</span>
                    </div>
                    <div className="emp-divs">
                        {
                            JSON.stringify(data.engagedEmp)
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}